import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseButton } from 'components/BaseButton';
import { MobileStep, MobileStepValue } from '../mobile-step-types';

const TOUCH_TARGET_STYLE = { minHeight: 44, minWidth: 44 };

const useStyles = makeStyles((theme: any) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(1)})`,
  },
  dragHandle: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.accent.main,
    alignSelf: 'center',
    marginBottom: theme.spacing(1),
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: 500,
    color: theme.palette.primary.main,
    outline: 'none',
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  actionGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
}));

type Props = {
  mobileStep: MobileStepValue;
  isMultiCharge: boolean;
  loadingSave: boolean;
  onNext: () => void;
  onBack: () => void;
  onSave: () => void;
  onClose: () => void;
};

export const MobileStepNavigation: FC<Props> = ({
  mobileStep,
  isMultiCharge,
  loadingSave,
  onNext,
  onBack,
  onSave,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, [mobileStep]);

  let title = t('charges:mobileStepSelectParties');
  if (mobileStep === MobileStep.SelectCharges) {
    title = t('charges:mobileStepSelectCharges');
  } else if (mobileStep === MobileStep.ConfigureAmounts) {
    title = t('charges:mobileStepConfigureAmounts');
  } else if (mobileStep === MobileStep.ReviewCharges) {
    title = t('charges:mobileStepReviewCharges');
  }

  const isFirstStep =
    mobileStep === MobileStep.SelectCharges ||
    (!isMultiCharge && mobileStep === MobileStep.SelectParties);
  const showBack = !isFirstStep;
  const showClose = isFirstStep;
  const showNext =
    mobileStep === MobileStep.SelectCharges ||
    mobileStep === MobileStep.SelectParties ||
    (mobileStep === MobileStep.ConfigureAmounts && isMultiCharge);
  const showSave =
    (mobileStep === MobileStep.ConfigureAmounts && !isMultiCharge) ||
    mobileStep === MobileStep.ReviewCharges;

  return (
    <Box className={classes.container}>
      <div className={classes.dragHandle} aria-hidden="true" role="presentation" />
      <h2 ref={titleRef} tabIndex={-1} className={classes.title}>
        {title}
      </h2>
      <Box className={classes.actions}>
        <Box className={classes.actionGroup}>
          {showBack && (
            <BaseButton
              buttonVariant="outlined"
              text={t('common:back', { defaultValue: 'Back' })}
              onClick={onBack}
              disabled={loadingSave}
              isLoading={false}
              customStyle={TOUCH_TARGET_STYLE}
            />
          )}
          {showClose && (
            <BaseButton
              buttonVariant="outlined"
              text={t('common:close', { defaultValue: 'Close' })}
              onClick={onClose}
              disabled={loadingSave}
              isLoading={false}
              customStyle={TOUCH_TARGET_STYLE}
            />
          )}
        </Box>
        <Box className={classes.actionGroup}>
          {showNext && (
            <BaseButton
              buttonVariant="contained"
              text={t('common:next', { defaultValue: 'Next' })}
              onClick={onNext}
              disabled={loadingSave}
              isLoading={false}
              customStyle={TOUCH_TARGET_STYLE}
            />
          )}
          {showSave && (
            <BaseButton
              buttonVariant="contained"
              text={t('common:save', { defaultValue: 'Save' })}
              onClick={onSave}
              disabled={loadingSave}
              isLoading={loadingSave}
              customStyle={TOUCH_TARGET_STYLE}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
